import { addLocale, locale, PrimeReactProvider } from "primereact/api";
import { type ReactNode, useEffect } from "react";
import { useClientTranslations } from "../../lib/i18n/client";

interface PrimeReactConfigProviderProps {
	children: ReactNode;
}

export function PrimeReactConfigProvider({
	children,
}: PrimeReactConfigProviderProps) {
	const { locale: currentLocale } = useClientTranslations();

	useEffect(() => {
		// Set up Spanish locale
		addLocale("es", {
			firstDayOfWeek: 1,
			dayNames: [
				"domingo",
				"lunes",
				"martes",
				"miércoles",
				"jueves",
				"viernes",
				"sábado",
			],
			dayNamesShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
			dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
			monthNames: [
				"enero",
				"febrero",
				"marzo",
				"abril",
				"mayo",
				"junio",
				"julio",
				"agosto",
				"septiembre",
				"octubre",
				"noviembre",
				"diciembre",
			],
			monthNamesShort: [
				"ene",
				"feb",
				"mar",
				"abr",
				"may",
				"jun",
				"jul",
				"ago",
				"sep",
				"oct",
				"nov",
				"dic",
			],
		});

		// Set up French locale
		addLocale("fr", {
			firstDayOfWeek: 1,
			dayNames: [
				"dimanche",
				"lundi",
				"mardi",
				"mercredi",
				"jeudi",
				"vendredi",
				"samedi",
			],
			dayNamesShort: ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
			dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
			monthNames: [
				"janvier",
				"février",
				"mars",
				"avril",
				"mai",
				"juin",
				"juillet",
				"août",
				"septembre",
				"octobre",
				"novembre",
				"décembre",
			],
			monthNamesShort: [
				"jan",
				"fév",
				"mar",
				"avr",
				"mai",
				"jun",
				"jul",
				"aoû",
				"sep",
				"oct",
				"nov",
				"déc",
			],
		});

		// Set up Hebrew locale
		addLocale("he", {
			firstDayOfWeek: 0,
			dayNames: ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"],
			dayNamesShort: ["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"],
			dayNamesMin: ["א", "ב", "ג", "ד", "ה", "ו", "ש"],
			monthNames: [
				"ינואר",
				"פברואר",
				"מרץ",
				"אפריל",
				"מאי",
				"יוני",
				"יולי",
				"אוגוסט",
				"ספטמבר",
				"אוקטובר",
				"נובמבר",
				"דצמבר",
			],
			monthNamesShort: [
				"ינו",
				"פבר",
				"מרץ",
				"אפר",
				"מאי",
				"יונ",
				"יול",
				"אוג",
				"ספט",
				"אוק",
				"נוב",
				"דצמ",
			],
		});

		// Set up Russian locale
		addLocale("ru", {
			firstDayOfWeek: 1,
			dayNames: [
				"воскресенье",
				"понедельник",
				"вторник",
				"среда",
				"четверг",
				"пятница",
				"суббота",
			],
			dayNamesShort: ["вс", "пн", "вт", "ср", "чт", "пт", "сб"],
			dayNamesMin: ["В", "П", "В", "С", "Ч", "П", "С"],
			monthNames: [
				"январь",
				"февраль",
				"март",
				"апрель",
				"май",
				"июнь",
				"июль",
				"август",
				"сентябрь",
				"октябрь",
				"ноябрь",
				"декабрь",
			],
			monthNamesShort: [
				"янв",
				"фев",
				"мар",
				"апр",
				"май",
				"июн",
				"июл",
				"авг",
				"сен",
				"окт",
				"ноя",
				"дек",
			],
		});
	}, []);

	// Update locale when language changes
	useEffect(() => {
		locale(currentLocale);
	}, [currentLocale]);

	const value = {
		locale: currentLocale,
	};

	return <PrimeReactProvider value={value}>{children}</PrimeReactProvider>;
}
